import { Link } from 'react-router-dom'
import { Instagram } from 'lucide-react'
import { useTranslation } from '../../core/hooks/useTranslation'
import { AppRoute } from '../../core/config/app-routes'

interface FooterProps {
  variant?: 'default' | 'compact' | 'menu'
  className?: string
}

export default function Footer({ variant = 'default', className = '' }: FooterProps) {
  const { t, isRTL } = useTranslation()

  // Compact variant - only copyright text (like in auth pages)
  if (variant === 'compact') {
    return (
      <footer className={`bg-gray-900 text-white py-4 ${className}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-gray-800 py-1 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} {t('siteName')}. {t('homepageFooterAllRightsReserved')}.</p>
          </div>
        </div>
      </footer>
    )
  }

  // Menu variant - uses same background as menu page
  if (variant === 'menu') {
    return (
      <footer className={`border-t border-gray-200 py-3 ${className}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Logo and Description */}
            <Link 
              to={AppRoute.HOME_ROUTE} 
              className="flex items-center gap-3 self-start group hover:opacity-80 transition-opacity"
            >
              <img
                src="/assets/images/logo.png"
                alt="Hatli Logo"
                className="h-10 w-auto"
              />
              <div>
                <div className="text-lg font-bold text-[#1E293B]">{t('siteName')}</div>
                <p className="text-xs text-gray-600">
                  {t('siteDescription')}
                </p>
              </div>
            </Link>

            {/* Contact */}
            <div className="flex items-center gap-4">

              {/* Copyright */}
              <div className={`text-sm text-gray-600 ${isRTL ? 'border-l' : 'border-r'} border-gray-300 ${isRTL ? 'pl-4' : 'pr-4'}`}>
                <p>{t('homepageFooterAllRightsReserved')} &copy; {new Date().getFullYear()}</p>
              </div>

              <div className="flex items-center gap-3">
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1877F2] hover:text-[#42A5F5] transition-colors"
                  aria-label={t('homepageContactFacebook')}
                  title={t('homepageContactFacebook')}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#E4405F] hover:text-[#F56040] transition-colors"
                  aria-label={t('homepageContactInstagram')}
                  title={t('homepageContactInstagram')}
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://wa.me/1234567890"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#25D366] hover:text-[#128C7E] transition-colors"
                  aria-label={t('homepageContactWhatsApp')}
                  title={t('homepageContactWhatsApp')}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                </a>
              </div>
              
              
            </div>
          </div>
        </div>
      </footer>
    )
  }

  // Default variant - full footer (like in HomePage)
  return (
    <footer className={`bg-gray-900 text-white py-8 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Logo and Description */}
          <div>
            <Link 
              to={AppRoute.HOME_ROUTE} 
              className="flex items-center gap-4 mb-3 group hover:opacity-80 transition-opacity"
            >
              <img
                src="/assets/images/logo_white.png"
                alt="Hatli Logo"
                className="h-16 w-auto"
              />
              <div>
                <div className="text-2xl font-bold mb-1">{t('siteName')}</div>
                <p className="text-xs text-gray-400">
                  {t('siteDescription')}
                </p>
              </div>
            </Link>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-sm mb-3">{t('homepageFooterQuickLinks')}</h3>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>
                <Link to="/register" className="hover:text-white transition-colors">
                  {t('homepageFooterRegister')}
                </Link>
              </li>
              <li>
                <Link to="/login" className="hover:text-white transition-colors">
                  {t('homepageSignIn')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-sm mb-3">{t('homepageFooterContact')}</h3>
            <div className="flex items-center gap-3">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#1877F2] hover:text-[#42A5F5] transition-colors"
                aria-label={t('homepageContactFacebook')}
                title={t('homepageContactFacebook')}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#E4405F] hover:text-[#F56040] transition-colors"
                aria-label={t('homepageContactInstagram')}
                title={t('homepageContactInstagram')}
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://wa.me/1234567890"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#25D366] hover:text-[#128C7E] transition-colors"
                aria-label={t('homepageContactWhatsApp')}
                title={t('homepageContactWhatsApp')}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-800 pt-6 text-center text-sm text-gray-400">
          <p>&copy; {new Date().getFullYear()} {t('siteName')}. {t('homepageFooterAllRightsReserved')}.</p>
        </div>
      </div>
    </footer>
  )
}

